import { Component, ElementRef, HostListener, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { OpsUnitService } from '@service/ops-unit.service';
import { ChannelCategory } from 'icreate/common/models/channel-category';
import { getValidationErrors, ValidationErrorMap } from 'icreate/common/validators';
import { MappedErrors } from 'icreate/common/validators/custom-functions';
import {
    opsColorLenLimit,
    opsUnitLocationLenLimit,
    opsUnitValidator,
} from 'icreate/common/validators/ops-unit/ops-unit-validator';
import { CATEGORY_MAX_LEN, ELLIPSIS } from 'icreate/common/models/ops-user';
import { MountType, OpsUnit } from 'icreate/common/models/ops-unit';
import * as _ from 'lodash';

export const opsUnitAllItemName = 'All';
type ChannelCategoryWithAll = Partial<ChannelCategory> & { selected: boolean; isAll: boolean };

export enum OpsUnitInputName {
    location = 'location',
    aLineIpAddress = 'LineAIp',
    bLineIpAddress = 'LineBIp',
    color = 'color',
}

enum RadioButtonOption {
    UNDEFINED,
    MOUNT,
    SUNSHADE,
    EMC,
    ETHERNET,
    KEYBOARD,
}

@Component({
    selector: 'app-ops-dialog',
    templateUrl: './ops-dialog.component.html',
    styleUrls: ['./ops-dialog.component.css'],
    standalone: false,
})
export class OpsDialogComponent {
    readonly LOCATION = OpsUnitInputName.location;
    readonly A_LINE_IP = OpsUnitInputName.aLineIpAddress;
    readonly B_LINE_IP = OpsUnitInputName.bLineIpAddress;
    readonly COLOR = OpsUnitInputName.color;
    readonly MOUNT_RADIO_BUTTON = RadioButtonOption.MOUNT;
    readonly SUNSHADE_RADIO_BUTTON = RadioButtonOption.SUNSHADE;
    readonly EMC_RADIO_BUTTON = RadioButtonOption.EMC;
    readonly ETHERNET_RADIO_BUTTON = RadioButtonOption.ETHERNET;
    readonly KEYBOARD_RADIO_BUTTON = RadioButtonOption.KEYBOARD;
    readonly CONSOLE = MountType.CONSOLE;
    readonly STAND = MountType.STAND;

    @ViewChild('categoryListContainer') categoryListContainer: ElementRef;

    validationErrors: ValidationErrorMap = {};
    openCategoryList: boolean = false;
    categoryLabel: string = opsUnitAllItemName;
    CATEGORY_MAX_LEN = CATEGORY_MAX_LEN;
    ELLIPSIS = ELLIPSIS;
    isLoading: boolean = false;

    private initialOpsUnit: OpsUnit;
    private initialCatList: ChannelCategoryWithAll[];
    private selectedCategories: ChannelCategoryWithAll[] = [];
    private areAllSelected: boolean;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            vesselId: string;
            categoryList: ChannelCategoryWithAll[];
            selectedCategoryNameList: string[];
            opsUnit: OpsUnit;
            allCategoriesSelected: boolean;
            isEditing: boolean;
            isDuplicating: boolean;
        },
        private dialogRef: MatDialogRef<OpsDialogComponent>,
        private opsUnitService: OpsUnitService,
        private translateService: TranslateService
    ) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: MouseEvent) {
        if (!this.categoryListContainer?.nativeElement.contains(event.target) && this.openCategoryList) {
            this.openCategoryList = false;
        }
    }

    ngOnInit(): void {
        this.initialOpsUnit = _.cloneDeep(this.data.opsUnit);
        this.data.categoryList = [
            {
                category: this.allCategoriesString,
                isAll: true,
                selected: this.data.selectedCategoryNameList.includes(opsUnitAllItemName),
            },
            ...this.data.categoryList.map(category => {
                return {
                    ...category,
                    selected: this.data.selectedCategoryNameList.includes(category.category),
                };
            }),
        ];

        this.updateCategoryLabel();
        this.updateSelectedCategories();
        this.initialCatList = _.cloneDeep(this.selectedCategories);
        if (this.data.isEditing || this.data.isDuplicating) this.checkInputErrors();
    }

    openSelect() {
        this.openCategoryList = !this.openCategoryList;
    }

    selectCategory(selectedCategory: ChannelCategoryWithAll | null) {
        const allItem = opsUnitAllItemName;
        const isSelectingAll = selectedCategory?.isAll && selectedCategory?.category === allItem;

        let selectedCount = 0;
        let firstSelectedLabel = '';

        this.data.categoryList = this.data.categoryList.map(category => {
            const isAllItem = category.category === allItem;

            if (isSelectingAll) {
                return {
                    ...category,
                    selected: isAllItem ? !category.selected : false,
                };
            }

            if (isAllItem) {
                return { ...category, selected: false };
            }

            if (category.id === selectedCategory?.id) {
                const selected = !category.selected;
                if (selected) {
                    selectedCount++;
                    if (!firstSelectedLabel) {
                        firstSelectedLabel = category.category;
                    }
                }
                return { ...category, selected };
            }

            if (category.selected) {
                selectedCount++;
                if (!firstSelectedLabel) {
                    firstSelectedLabel = category.category;
                }
            }

            return category;
        });
        this.updateCategoryLabel();
        this.updateSelectedCategories();
    }

    updateCategoryLabel() {
        const selected = this.data.categoryList.filter(c => c.selected);
        if (selected.length == 0) {
            this.categoryLabel = '';
            return;
        }
        const allCategory = selected.find(cat => cat.isAll);
        if (allCategory) {
            this.categoryLabel = this.allCategoriesString;
        } else if (selected.length === 1) {
            const categoryName = selected[0].category;
            this.categoryLabel =
                categoryName.length > CATEGORY_MAX_LEN
                    ? categoryName.slice(0, CATEGORY_MAX_LEN) + ELLIPSIS
                    : categoryName;
        } else {
            const categoryName = selected[0].category;
            const shortName =
                categoryName.length > CATEGORY_MAX_LEN
                    ? categoryName.slice(0, CATEGORY_MAX_LEN) + ELLIPSIS
                    : categoryName;

            this.categoryLabel = `${shortName} (+${selected.length - 1})`;
        }
    }

    updateSelectedCategories() {
        this.selectedCategories = [];
        this.areAllSelected = false;
        const allCategory = this.data.categoryList.find(cat => cat.isAll);
        if (allCategory && allCategory.selected) {
            this.areAllSelected = true;
            this.selectedCategories = this.data.categoryList.slice(1);
            return;
        }
        for (let i = 0; i < this.data.categoryList.length; i++) {
            const category = this.data.categoryList[i];
            if (category.selected && !category.isAll) {
                this.selectedCategories.push(this.data.categoryList[i]);
            }
        }
    }

    onInputChange(event: { name; value }) {
        const { name, value } = event;
        this.data.opsUnit[name] = value;
        this.checkInputErrors();
    }

    close() {
        this.dialogRef.close(false);
    }

    checkInputErrors() {
        const errors = getValidationErrors(opsUnitValidator(), {
            location: this.data?.opsUnit?.location,
            LineAIp: this.data?.opsUnit?.LineAIp,
            LineBIp: this.data?.opsUnit?.LineBIp,
            index: this.data?.opsUnit?.index,
            allCategoriesSelected: this.data.allCategoriesSelected,
            mountType: this.data.opsUnit?.mountType,
            sunshade: this.data.opsUnit?.sunshade,
            EMC: this.data.opsUnit?.EMC,
            ethernetExtraLength: this.data.opsUnit?.ethernetExtraLength,
            keyboard: this.data.opsUnit?.keyboard,
            color: this.data.opsUnit?.color,
        });

        if (errors) {
            const mappedErrors: MappedErrors = {};
            for (let i = 0; i < errors.length; i++) {
                const errorObj = mappedErrors[errors[i].path];
                if (errors[i].message === 'errors.validation.generic.above-maximum') {
                    if (errors[i].path == OpsUnitInputName.location) {
                        errors[i].message = this.translateService.instant('errors.validation.generic.above-maximum', {
                            max: opsUnitLocationLenLimit,
                        });
                    } else if (errors[i].path == OpsUnitInputName.color) {
                        errors[i].message = this.translateService.instant('errors.validation.generic.above-maximum', {
                            max: opsColorLenLimit,
                        });
                    }
                }

                if (!errorObj) mappedErrors[errors[i].path] = errors[i];
                else mappedErrors[errors[i].path].errors = [...errorObj.errors, errors[i].message];
            }

            this.validationErrors = mappedErrors;
        }
    }

    updateRadioButtonValue = (radioButton: RadioButtonOption, value) => {
        if (radioButton == RadioButtonOption.MOUNT) this.data.opsUnit.mountType = value;
        else if (radioButton == RadioButtonOption.SUNSHADE) this.data.opsUnit.sunshade = value;
        else if (radioButton == RadioButtonOption.EMC) this.data.opsUnit.EMC = value;
        else if (radioButton == RadioButtonOption.ETHERNET) this.data.opsUnit.ethernetExtraLength = value;
        else if (radioButton == RadioButtonOption.KEYBOARD) this.data.opsUnit.keyboard = value;
    };

    async saveOps() {
        if (this.isSaveButtonDisabled || this.isLoading) return;

        try {
            this.isLoading = true;
            const opsUnitData = {
                id: this.data.opsUnit?.id,
                vesselId: this.data.vesselId,
                categories: this.selectedCategories,
                index: this.data.opsUnit?.index,
                location: this.data.opsUnit?.location,
                LineAIp: this.data.opsUnit?.LineAIp,
                LineBIp: this.data.opsUnit?.LineBIp,
                allCategoriesSelected: this.areAllSelected,
                mountType: this.data.opsUnit?.mountType,
                sunshade: this.data.opsUnit?.sunshade,
                EMC: this.data.opsUnit?.EMC,
                ethernetExtraLength: this.data.opsUnit?.ethernetExtraLength,
                keyboard: this.data.opsUnit?.keyboard,
                color: this.data.opsUnit?.color,
            };
            if (this.data.isEditing) await this.opsUnitService.update(opsUnitData);
            else await this.opsUnitService.create(opsUnitData);
            this.isLoading = false;
            this.dialogRef.close(true);
        } catch (error) {
            this.isLoading = false;
            console.log(error);
        }
    }

    get allCategoriesString() {
        return this.translateService.instant('system-settings.ops.categories.all');
    }

    get isSaveButtonDisabled() {
        if (this.selectedCategories?.length == 0) return true;
        if (this.data.isEditing) {
            if (!_.isEqual(this.selectedCategories, this.initialCatList)) return false;
            else if (_.isEqual(this.data.opsUnit, this.initialOpsUnit)) return true;
        }
        return !this.data.opsUnit?.location?.length || !!Object.keys(this.validationErrors).length;
    }
}
